import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch,
  Popconfirm, message, Card, Space, Tag, Typography, Tooltip
} from 'antd';
import { PlusOutlined, DeleteOutlined, CopyOutlined, LinkOutlined, MobileOutlined, EditOutlined } from '@ant-design/icons';
import { getFiles, addFile, updateFile, deleteFile } from '../services/api';

const { Text } = Typography;

function FilesPage() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, [page, pageSize]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getFiles({ page, page_size: pageSize });
      setData(res.data.items);
      setTotal(res.data.total);
    } catch (err) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (values) => {
    setSubmitting(true);
    try {
      const payload = { ...values, mobile_only: !!values.mobile_only };
      await addFile(payload);
      message.success('创建成功');
      setModalVisible(false);
      form.resetFields();
      loadData();
    } catch (err) {
      message.error(err.response?.data?.error || '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      s3_bucket: record.s3_bucket || '',
      s3_region: record.s3_region || '',
      s3_key: record.s3_key || '',
      filename: record.filename || '',
      redirect_url: record.redirect_url || '',
      mobile_only: !!record.mobile_only,
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async (values) => {
    setSubmitting(true);
    try {
      const payload = { ...values, mobile_only: !!values.mobile_only };
      await updateFile(editingRecord.id, payload);
      message.success('更新成功');
      setEditModalVisible(false);
      editForm.resetFields();
      setEditingRecord(null);
      loadData();
    } catch (err) {
      message.error(err.response?.data?.error || '更新失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteFile(id);
      message.success('已删除');
      loadData();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const getApiBase = () => {
    const envBase = process.env.REACT_APP_API_BASE;
    if (envBase) return envBase;
    return window.location.origin;
  };

  const copyLink = (fileId, type) => {
    const base = getApiBase();
    const url = type === 'secure' ? `${base}/d/${fileId}` : `${base}/dl/${fileId}`;
    navigator.clipboard.writeText(url).then(() => {
      message.success(`${type === 'secure' ? '安全链接' : '直链'}已复制`);
    }).catch(() => {
      message.info(url);
    });
  };

  const columns = [
    {
      title: '文件ID',
      dataIndex: 'file_id',
      key: 'file_id',
      width: 100,
      render: (val) => <Tag color="blue">{val}</Tag>
    },
    {
      title: '类型',
      key: 'type',
      width: 80,
      render: (_, record) => record.redirect_url
        ? <Tag color="green">跳转</Tag>
        : <Tag color="purple">S3</Tag>
    },
    {
      title: 'S3 Key / 跳转URL',
      key: 's3_or_redirect',
      ellipsis: true,
      render: (_, record) => record.redirect_url
        ? <Tooltip title={record.redirect_url}><Text style={{ fontSize: 12, color: '#52c41a' }}>{record.redirect_url}</Text></Tooltip>
        : <Tooltip title={record.s3_key}><Text style={{ fontSize: 12 }}>{record.s3_key || '-'}</Text></Tooltip>
    },
    {
      title: '文件名',
      dataIndex: 'filename',
      key: 'filename',
      width: 120,
      render: (val) => val ? <Text style={{ fontSize: 12 }}>{val}</Text> : '-'
    },
    {
      title: '仅移动端',
      dataIndex: 'mobile_only',
      key: 'mobile_only',
      width: 80,
      render: (val) => val ? <Tag icon={<MobileOutlined />} color="orange">是</Tag> : <Text type="secondary">否</Text>
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (val) => <Text type="secondary" style={{ fontSize: 11 }}>{val}</Text>
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="复制安全链接 (/d/)">
            <Button
              type="text"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => copyLink(record.file_id, 'secure')}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="复制直链 (/dl/)">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyLink(record.file_id, 'direct')}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{ color: '#faad14' }}
            />
          </Tooltip>
          <Popconfirm
            title="确认删除此映射？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card size="small" bordered={false} style={{ marginBottom: 12, borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            共 {total} 个文件映射
          </Text>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            新建映射
          </Button>
        </div>
      </Card>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          size: 'small',
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); }
        }}
      />

      {/* 新建映射 Modal */}
      <Modal
        title="新建文件映射"
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleAdd} style={{ marginTop: 16 }}>
          <Form.Item
            name="file_id"
            label="文件ID（留空自动生成）"
          >
            <Input placeholder="自定义短链ID，如: bitpie" />
          </Form.Item>

          <Form.Item name="redirect_url" label="跳转URL（填写后将不走S3，直接跳转）">
            <Input placeholder="如: https://example.com/download/app.apk" />
          </Form.Item>

          <Form.Item name="s3_bucket" label="S3 Bucket（留空使用默认）">
            <Input placeholder="如: my-download-bucket" />
          </Form.Item>
          <Form.Item name="s3_region" label="S3 Region（留空使用默认）">
            <Input placeholder="如: us-east-1, ap-southeast-1" />
          </Form.Item>
          <Form.Item
            name="s3_key"
            label="S3 Object Key（跳转模式可留空）"
          >
            <Input placeholder="如: BitPieWallet.apk" />
          </Form.Item>
          <Form.Item name="filename" label="下载文件名">
            <Input placeholder="可选: 用户下载时显示的文件名" />
          </Form.Item>

          <Form.Item name="mobile_only" label="仅限移动端访问" valuePropName="checked">
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={submitting} block>
              确认创建
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑映射 Modal */}
      <Modal
        title={`编辑文件映射 - ${editingRecord?.file_id || ''}`}
        open={editModalVisible}
        onCancel={() => { setEditModalVisible(false); editForm.resetFields(); setEditingRecord(null); }}
        footer={null}
        width={500}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdate} style={{ marginTop: 16 }}>
          <Form.Item name="redirect_url" label="跳转URL（填写后将不走S3，直接跳转）">
            <Input placeholder="如: https://example.com/download/app.apk" />
          </Form.Item>

          <Form.Item name="s3_bucket" label="S3 Bucket（留空使用默认）">
            <Input placeholder="如: my-download-bucket" />
          </Form.Item>
          <Form.Item name="s3_region" label="S3 Region（留空使用默认）">
            <Input placeholder="如: us-east-1, ap-southeast-1" />
          </Form.Item>
          <Form.Item
            name="s3_key"
            label="S3 Object Key（跳转模式可留空）"
          >
            <Input placeholder="如: BitPieWallet.apk" />
          </Form.Item>
          <Form.Item name="filename" label="下载文件名">
            <Input placeholder="可选: 用户下载时显示的文件名" />
          </Form.Item>

          <Form.Item name="mobile_only" label="仅限移动端访问" valuePropName="checked">
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={submitting} block>
              确认更新
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default FilesPage;
